import axios from "axios";
import { readStoredSettings } from "@/lib/userSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export async function ensureServiceWorkerRegistration() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.register("/service-worker.js");
  await navigator.serviceWorker.ready;
  return registration;
}

export async function syncPushSubscription(token) {
  if (typeof window === "undefined") return false;
  if (!token || !("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;

  const settings = readStoredSettings();
  if (settings.browserNotifications !== true) return false;

  const keyResponse = await axios.get(`${API}/notifications/push/public-key`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const publicKey = keyResponse.data?.public_key;
  if (!publicKey) return false;

  const registration = await ensureServiceWorkerRegistration();
  if (!registration) return false;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await axios.post(
    `${API}/notifications/push/subscribe`,
    { subscription: subscription.toJSON() },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  return true;
}

export async function disablePushSubscription(token) {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return false;
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  if (!registration) return false;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return false;

  const endpoint = subscription.endpoint;
  try {
    if (token && endpoint) {
      await axios.delete(`${API}/notifications/push/subscribe`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { endpoint },
      });
    }
  } catch {
    // best effort
  }

  await subscription.unsubscribe();
  return true;
}

export async function showServiceWorkerNotification(title, options = {}, targetPath = "/") {
  if (typeof window === "undefined") return null;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return null;

  const registration = await ensureServiceWorkerRegistration();
  if (registration?.showNotification) {
    await registration.showNotification(title, {
      body: options.body,
      icon: "/assets/logo-vertical.png",
      badge: "/assets/logo-vertical.png",
      tag: options.tag,
      data: { url: targetPath },
    });
    return true;
  }

  const notification = new Notification(title, {
    icon: "/assets/logo-vertical.png",
    badge: "/assets/logo-vertical.png",
    tag: options.tag,
    body: options.body,
    silent: false,
  });

  notification.onclick = () => {
    window.focus();
    window.location.assign(targetPath);
    notification.close();
  };

  return notification;
}
